import type { FC } from 'react'
import type { DivingDad } from '../content/divingDads'
import { getMoveById } from '../content/divingDads'

type DadCardProps = {
  readonly dad: DivingDad
}

const DadCard: FC<DadCardProps> = ({ dad }) => {
  const attempted = dad.attemptedMoveIds.length
  const landed = dad.completedMoveIds.length
  const inProgress = attempted - landed
  const progress =
    attempted === 0 ? 0 : Math.round((landed / attempted) * 100)

  const scrollToMove = (moveId: string) => {
    document.getElementById(`move-${moveId}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    })
  }

  return (
    <article className='diving-dads-card' id={`dad-${dad.id}`}>
      <h3 className='diving-dads-card__name'>{dad.name}</h3>
      <p className='diving-dads-card__role'>{dad.role}</p>
      <p className='diving-dads-card__move'>
        Signature move: <em>{dad.signatureMove}</em>
      </p>

      <div className='deck-log' aria-label={`${dad.name} deck log`}>
        <div className='deck-log__header'>
          <span className='deck-log__title'>Deck log</span>
          <span className='deck-log__stats'>
            <span className='deck-log__stat deck-log__stat--splash'>
              {attempted} splashes
            </span>
            <span className='deck-log__divider' aria-hidden='true'>
              ·
            </span>
            <span className='deck-log__stat deck-log__stat--landed'>
              {landed} landed
            </span>
          </span>
        </div>
        <div
          className='deck-log__track'
          role='progressbar'
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${landed} of ${attempted} moves landed`}
        >
          <span
            className='deck-log__fill deck-log__fill--splash'
            style={{ width: '100%' }}
          />
          <span
            className='deck-log__fill deck-log__fill--landed'
            style={{ width: `${progress}%` }}
          />
        </div>
        {inProgress > 0 && (
          <p className='deck-log__hint'>
            {inProgress} still in the splash zone
          </p>
        )}
        <ul className='deck-log__pills' aria-label='Move status'>
          {dad.attemptedMoveIds.map((moveId) => {
            const move = getMoveById(moveId)
            const landedMove = dad.completedMoveIds.includes(moveId)
            if (!move) return null
            return (
              <li key={moveId}>
                <button
                  type='button'
                  className={`move-pill move-pill--${landedMove ? 'landed' : 'splash'}`}
                  onClick={() => scrollToMove(moveId)}
                  title={
                    landedMove
                      ? `${move.name} — landed`
                      : `${move.name} — attempted, not yet landed`
                  }
                >
                  <span className='move-pill__icon' aria-hidden='true'>
                    {landedMove ? '✓' : '○'}
                  </span>
                  <span className='move-pill__label'>{move.name}</span>
                </button>
              </li>
            )
          })}
        </ul>
      </div>

      {dad.aspirationMoveIds.length > 0 && (
        <div className='dad-aspirations'>
          <p className='dad-aspirations__label'>Still chasing</p>
          <ul className='dad-aspirations__list'>
            {dad.aspirationMoveIds.map((moveId) => {
              const move = getMoveById(moveId)
              if (!move) return null
              return (
                <li key={moveId}>
                  <button
                    type='button'
                    className='aspiration-pill'
                    onClick={() => scrollToMove(moveId)}
                  >
                    {move.name}
                    <span className='aspiration-pill__arrow' aria-hidden='true'>
                      →
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </article>
  )
}

export default DadCard
